import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { store } from '../store'
import { logout } from '../store/slices/authSlice'
import { getToken } from './auth'

// API configuration
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1'
const API_TIMEOUT = 30000 // 30 seconds

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add authentication token
apiClient.interceptors.request.use(
  config => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add request ID for tracing
    config.headers['X-Request-ID'] = generateRequestId()

    // Add timestamp
    config.headers['X-Request-Time'] = new Date().toISOString()

    return config
  },
  error => {
    console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log successful responses in development
    if (import.meta.env.DEV) {
      console.log(
        `API Response [${response.config.method?.toUpperCase()}] ${response.config.url}:`,
        response.status
      )
    }
    return response
  },
  error => {
    console.error('API Error:', error)

    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.warn('Unauthorized access, logging out user')
          store.dispatch(logout())
          break
        case 403:
          // Forbidden - user doesn't have permission
          console.warn(
            'Access forbidden:',
            data?.message || 'Insufficient permissions'
          )
          break
        case 404:
          // Not found
          console.warn('Resource not found:', error.config?.url)
          break
        case 429:
          // Rate limited
          console.warn('Rate limit exceeded, please try again later')
          break
        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error(
            'Server error:',
            status,
            data?.message || 'Internal server error'
          )
          break
        default:
          console.error(
            'Unexpected API error:',
            status,
            data?.message || 'Unknown error'
          )
      }

      // Transform error response for consistent error handling
      const apiError = {
        status,
        message: data?.message || getDefaultErrorMessage(status),
        code: data?.code,
        details: data?.details,
        timestamp: new Date().toISOString(),
        requestId: error.config?.headers?.['X-Request-ID'],
      }

      return Promise.reject(apiError)
    } else if (error.request) {
      // Network error
      console.error('Network error - no response received:', error.request)
      return Promise.reject({
        status: 0,
        message: 'Network error - please check your connection',
        timestamp: new Date().toISOString(),
      })
    } else {
      // Request setup error
      console.error('Request setup error:', error.message)
      return Promise.reject({
        status: -1,
        message: error.message || 'Request failed to send',
        timestamp: new Date().toISOString(),
      })
    }
  }
)

// Helper function to generate unique request IDs
const generateRequestId = (): string => {
  return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Helper function to get default error messages
const getDefaultErrorMessage = (status: number): string => {
  const messages: Record<number, string> = {
    400: 'Bad request - please check your input',
    401: 'Authentication required',
    403: 'Access forbidden - insufficient permissions',
    404: 'Resource not found',
    408: 'Request timeout - please try again',
    409: 'Conflict - resource already exists or is in use',
    422: 'Invalid data provided',
    429: 'Too many requests - please try again later',
    500: 'Internal server error - please try again later',
    502: 'Bad gateway - service temporarily unavailable',
    503: 'Service unavailable - please try again later',
    504: 'Gateway timeout - please try again later',
  }

  return messages[status] || `HTTP ${status} error`
}

// API response types
export interface ApiResponse<T = unknown> {
  data: T
  message?: string
  success: boolean
  timestamp: string
}

export interface PaginatedResponse<T = unknown> {
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
  message?: string
  success: boolean
  timestamp: string
}

export interface ApiError {
  status: number
  message: string
  code?: string
  details?: unknown
  timestamp: string
  requestId?: string
}

// Generic API methods
export const api = {
  // GET request
  get: async <T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.get<ApiResponse<T>>(url, config)
    return response.data
  },

  // POST request
  post: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config)
    return response.data
  },

  // PUT request
  put: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data, config)
    return response.data
  },

  // PATCH request
  patch: async <T = unknown>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data, config)
    return response.data
  },

  // DELETE request
  delete: async <T = unknown>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete<ApiResponse<T>>(url, config)
    return response.data
  },

  // Upload file
  upload: async <T = unknown>(
    url: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<T>> => {
    const formData = new FormData()
    formData.append('file', file)

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: progressEvent => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          onProgress(progress)
        }
      },
    }

    const response = await apiClient.post<ApiResponse<T>>(url, formData, config)
    return response.data
  },

  // Download file
  download: async (url: string, filename?: string): Promise<void> => {
    const response = await apiClient.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  },
}

// Export the axios instance for direct use if needed
export { apiClient }
export default api
